import * as React from 'react';
import FileUploader from '../components/uploads/FileUploader';
import { add, format, toDate } from 'date-fns';
import { classNames } from 'primereact/utils';
import { useAppDispatch, useAppSelector } from '../store';
import { addFileToTimePeriod, createTimePeriod, fetchTimePeriod, fetchUploadHistory } from '../store/upload';
import UploadConfirmModal from '../components/uploads/UploadConfirmModal';
import { Message } from 'primereact/message';
import { getMimeTypesArr, MimeType } from '../utils/upload';
import { APIStatus, OptionValue } from '../utils/types';
import UploadHistoryList from '../components/uploads/UploadHistoryList';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { flattenTimePeriodDate } from '../utils/date';
import { TimePeriodServiceGetResponse } from '../services/api';

export default function UploadPage() {  
  const [firstOfCurrentMonth] = React.useState(toDate((new Date()).setDate(1)));
  const [selectedUploadMonth, setSelectedUploadMonth] = React.useState<number>(parseInt(format(firstOfCurrentMonth, 'M'), 10) - 1);
  const [selectedUploadYear, setSelectedUploadYear] = React.useState<number>(parseInt(format(firstOfCurrentMonth, 'yyyy'), 10));
  const [timePeriod, setTimePeriod] = React.useState<TimePeriodServiceGetResponse>();
  const [uploadError, setUploadError] = React.useState<string|null>(null);

  const dispatch = useAppDispatch();
  const { queuedUploads, uploadState, showFileUploadPreview, uploadedFiles, apiState, uploadHistory } = useAppSelector((state) => state.uploads);

  function getSelectedDateAsDate() {
    const tpDate = new Date();
    tpDate.setMonth(selectedUploadMonth);
    tpDate.setFullYear(selectedUploadYear);
    tpDate.setDate(1);
    tpDate.setHours(0, 0, 0, 0);

    return flattenTimePeriodDate(tpDate);
  }

  React.useEffect(() => {
    dispatch(fetchUploadHistory(getSelectedDateAsDate()));
    fetchCurrentTimePeriod(getSelectedDateAsDate());
  }, [selectedUploadMonth, selectedUploadYear]);

  async function fetchCurrentTimePeriod(timePeriodDate: Date) {
    const newTimePeriod = await fetchTimePeriod({ timePeriodDate });
    setTimePeriod(newTimePeriod);
  }
  
  const prevYear = add(toDate(firstOfCurrentMonth), { years: -1 }).getFullYear();
  const nextYear = add(toDate(firstOfCurrentMonth), { years: 1 }).getFullYear();
  const yearOptions: OptionValue[] = [
    {
      value: prevYear,
      label: prevYear.toString(),
    },
    {
      label: firstOfCurrentMonth.getFullYear().toString(),
      value: parseInt(format(firstOfCurrentMonth, 'yyyy'), 10)
    },
    {
      value: nextYear,
      label: nextYear.toString(),
    },
  ];

  const allowedMimeTypes = [MimeType.XLS, MimeType.XLSX];
  
  async function addGroupDataFile(files: File[]) {
    setUploadError(null);

    // validate the files uploaded
    // there should be no more than 2
    if (files.length > 2) {
      setUploadError('No more than 2 files are allowed to be uploaded at a time');
      return;
    }

    const validTypes = getMimeTypesArr(allowedMimeTypes);

    // validate mime type
    for (const i in files) {
      if (!validTypes.includes(files[i].type)) {
        setUploadError('An invalid file type was uploaded, please ensure only XLSX and XLS files are uploaded');
        return;
      }
    }

    // TODO: validate file contents, via API?

    if (!uploadError) {
      const timePeriodDate = getSelectedDateAsDate();

      // verify time period exists
      try {
        await fetchTimePeriod({ timePeriodDate });
      } catch (error) {
        await createTimePeriod({ timePeriodDate });
      }

      dispatch(addFileToTimePeriod({
        files,
        timePeriodDate,
      }));
    }
  }

  function handleUploadComplete() {
    dispatch(fetchUploadHistory(getSelectedDateAsDate()));
  }

  function DateSelector() {
    return (
      <div className={
        classNames(
          "flex flex-col gap-4",
          {
            "opacity-50 pointer-events-none": apiState === APIStatus.LOADING,
          }
        )
      }>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="upload-month_select" className="hidden">Year</label>
            <Dropdown
              options={yearOptions}
              optionLabel="label"
              optionValue="value"
              value={selectedUploadYear}
              onChange={(e) => setSelectedUploadYear(e.value)}
              valueTemplate={
                <span className="flex items-center gap-2">
                  <span className="pi pi-calendar text-allstate-blue"></span>
                  <span className="flex gap-1">
                    <span>Reissue year:</span>
                    <strong>{selectedUploadYear}</strong>
                  </span>
                </span>
              }
            />
          </div>
          <div className="text-xs text-grey-8">Select the reissue month below</div>
        </div>

        <div className="grid items-center grid-cols-12 gap-2">
          {[...Array(12).keys()].map((i) => {
            const month = new Date();
            month.setDate(1);
            month.setMonth(i);

            const isSelectedMonth = selectedUploadMonth === parseInt(format(month, 'M'), 10) - 1;

            return (
              <div
                key={i}
                className={
                    classNames(
                      'border-b-4',
                      'py-1.5',
                      'cursor-pointer',
                      'text-center',
                      'text-lg',
                      {
                        'border-allstate-light-blue': isSelectedMonth,
                        'border-grey-7 text-grey-7': !isSelectedMonth
                      }
                    )
                  }
                  onClick={() => setSelectedUploadMonth(parseInt(format(month, 'M'), 10) - 1)}
              >
                <strong>{format(month, 'LLL')}</strong>
              </div>
            )
          })}
        </div>
        <div className="text-xs">Currently selected: <strong>{format(getSelectedDateAsDate(), 'MMMM yyyy')}</strong> reissue date</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <section>
        <h1 className="text-xl font-medium text-allstate-blue">Data import</h1>
        <p>Upload group and BAFO data</p>
      </section>

      <section className="p-6 bg-white">
        <DateSelector />
      </section>

      {uploadError && (
        <Message severity="error" text={uploadError} />
      )}

      <div className="relative">
        {uploadState === APIStatus.LOADING && (
          <div className="absolute inset-0 z-10 flex items-center rounded bg-grey-2/50">
            <ProgressSpinner className="w-8 h-8" />
          </div>
        )}
        <FileUploader className="p-6 bg-white rounded-lg" onAddFile={addGroupDataFile} files={uploadedFiles?.files} allowedMimeTypes={allowedMimeTypes} />
      </div>

      {showFileUploadPreview && queuedUploads && (
        <UploadConfirmModal onComplete={handleUploadComplete} />
      )}

      <section className="flex flex-col gap-2 p-6 bg-white rounded-lg">
        <h2 className="font-bold">Version history</h2>

        {apiState === APIStatus.LOADING && <ProgressSpinner className="w-8 h-8" />}
        {apiState === APIStatus.ERROR && <p className="text-warning-red">There was an error loading the upload history, please try again later</p>}
        {apiState === APIStatus.COMPLETE && !!uploadHistory.length && <UploadHistoryList uploads={uploadHistory} timePeriod={timePeriod} />}
        {apiState === APIStatus.COMPLETE && !uploadHistory.length && <p className="text-sm">No files are available for this period</p>}        
      </section>
    </div>
  ); 
}